/**
 * ErrorBoundary
 *
 * Description:
 * Catches render, lifecycle and constructor errors thrown by anything inside
 * it, reports them to the backend through helpers/error-logger.js, and renders
 * nothing in place of the broken part.
 *
 * Wrapped around <Outlet /> in pages/themeLayout.js, which covers every page
 * because all routes are children of that layout.
 *
 * Note: React error boundaries cannot catch errors thrown inside event
 * handlers, setTimeout callbacks or async code. For those, call logError()
 * from helpers/error-logger.js inside the catch block by hand.
 */

import React from "react";
import { useLocation } from "react-router-dom";
import { logError } from "../helpers/error-logger";

class ErrorBoundaryCore extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logError(error, this.props.name, errorInfo, "COMPONENT_ERROR");
  }

  componentDidUpdate(prevProps) {
    // Clear the error when the guest moves to another route, otherwise the
    // broken state would stay for the rest of the session.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

/* Wrapper so the boundary can reset itself on a route change */
const ErrorBoundary = ({ name, children }) => {
  const location = useLocation();
  return (
    <ErrorBoundaryCore name={name} resetKey={location.pathname}>
      {children}
    </ErrorBoundaryCore>
  );
};

export default ErrorBoundary;
